import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SettingsMap } from '@workforce/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useTz } from '@/lib/timezone';
import { t } from '@/lib/i18n';
import { TimeZoneSelect } from '@/components/TimeZoneSelect';
import { Button, Card, ErrorBanner, Field, Input, Notice, PageHeader, Select, Spinner, Toggle } from '@/components/ui';

interface Def {
  key: keyof SettingsMap;
  group: string;
  label: string;
  description: string;
  default: unknown;
  options?: string[];
}

const OPTION_LABELS: Record<string, string> = {
  calendar: 'Calendar year (1 Jan – 31 Dec)',
  fixed_date: 'Fixed start date every year',
  anniversary: "Each employee's work anniversary",
  first_last: 'First sign-in → last sign-out',
  sum_intervals: 'Sum of all worked intervals',
  notify: 'Check daily and notify administrators (recommended)',
  auto: 'Check daily and apply changes automatically',
  off: "Don't check automatically",
  clocked_in: 'Only while clocked in (recommended)',
  schedule: 'Only during scheduled hours',
  always: 'Whenever the computer is in use',
  neutral: 'Neutral',
  productive: 'Productive',
  unproductive: 'Unproductive',
  percentage: 'Pay the percentage of hours completed (97 of 100 h = 97%)',
  deduct: 'Deduct missing hours at the normal hourly rate',
  none: 'Pay the full salary anyway',
  whole: 'Whole units (e.g. 4,933)',
  cents: 'Two decimals (e.g. 4,932.79)',
  end_month: 'The month it ends in (26 Aug → 25 Sep = September)',
  start_month: 'The month it starts in (26 Aug → 25 Sep = August)',
  'company.default_language:en': 'English',
  'company.default_language:ar': 'Arabic',
  // Per-setting labels (key:value) win over the general ones above.
  'attendance.computer_hours_count:active': 'Time in use, idle excluded (like Time Doctor)',
  'attendance.computer_hours_count:active_no_unproductive': 'Time in use, idle and unproductive excluded',
  'attendance.computer_hours_count:productive': 'Productive time only',
  'attendance.computer_hours_count:tracked': 'Everything recorded, idle included',
  'leave.absence_uses_balance:annual_then_sick': 'From annual leave, then sick leave without a report, then unpaid',
  'leave.absence_uses_balance:annual': 'From annual leave, then unpaid',
  'leave.absence_uses_balance:none': 'Always unpaid',
  everyone: 'Everyone',
  non_office: 'Only remote / hybrid staff',
  nobody: 'Nobody (fingerprint / door QR only)',
};

export function SettingsPage() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const q = useQuery({ queryKey: ['settings'], queryFn: () => api.get<{ values: SettingsMap; definitions: Def[] }>('/settings') });
  const [values, setValues] = useState<Partial<SettingsMap>>({});
  const [reason, setReason] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (q.data) setValues(q.data.values);
  }, [q.data]);

  const changed = useMemo(() => {
    if (!q.data) return {};
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) if (JSON.stringify(v) !== JSON.stringify(q.data.values[k as keyof SettingsMap])) out[k] = v;
    return out;
  }, [values, q.data]);

  const save = useMutation({
    mutationFn: () => api.patch('/settings', { values: changed, reason: reason || undefined }),
    onSuccess: () => {
      setSaved(true);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void refresh();
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors() : {};

  if (q.isLoading) return <Spinner />;
  if (!q.data) return <ErrorBanner error={q.error} />;
  const groups = [...new Set(q.data.definitions.map((d) => d.group))];

  const input = (d: Def) => {
    const v = values[d.key];
    const set = (nv: unknown) => {
      setSaved(false);
      setValues((s) => ({ ...s, [d.key]: nv }));
    };
    if (typeof d.default === 'boolean') return <Toggle checked={!!v} onChange={set} label={t(d.label)} description={t(d.description)} />;
    if (d.key === 'company.timezone') return <TimeZoneSelect value={String(v ?? '')} onChange={set} />;
    if (d.key === 'company.logo') return <LogoInput value={String(v ?? '')} onChange={set} />;
    if (d.options)
      return (
        <Select value={String(v)} onChange={(e) => set(e.target.value)}>
          {d.options.map((o) => (
            <option key={o} value={o}>
              {t(OPTION_LABELS[`${d.key}:${o}`] ?? OPTION_LABELS[o] ?? o)}
            </option>
          ))}
        </Select>
      );
    if (d.key.endsWith('_date')) return <Input type="date" value={String(v ?? '')} onChange={(e) => set(e.target.value)} />;
    if (typeof d.default === 'number') return <Input type="number" value={Number(v ?? 0)} onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))} invalid={!!fe[d.key]} />;
    return <Input value={String(v ?? '')} onChange={(e) => set(e.target.value)} invalid={!!fe[d.key]} />;
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title={t('Settings')} subtitle={t('Company-wide rules. Every change is recorded in the audit log.')} />
      <ErrorBanner error={save.error} />
      {saved && (
        <div className="mb-4">
          <Notice tone="green">{t('Settings saved.')}</Notice>
        </div>
      )}
      <div className="space-y-6">
        {groups.map((g) => (
          <Card key={g} title={t(g)}>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {q.data.definitions
                .filter((d) => d.group === g)
                .map((d) =>
                  typeof d.default === 'boolean' ? (
                    <div key={d.key} className="sm:col-span-2">
                      {input(d)}
                    </div>
                  ) : (
                    <Field key={d.key} label={t(d.label)} hint={t(d.description)} error={fe[d.key]} className={d.key === 'company.timezone' || d.key === 'company.logo' ? 'sm:col-span-2' : ''}>
                      {input(d)}
                    </Field>
                  ),
                )}
            </div>
          </Card>
        ))}
        <BackupsCard />
      </div>
      <div className="sticky bottom-0 mt-6 flex flex-col gap-2 border-t border-slate-200 bg-slate-50/95 py-4 backdrop-blur sm:flex-row sm:items-center">
        <Input placeholder={t('Reason for change (optional, recorded in audit log)')} value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1" />
        <Button loading={save.isPending} disabled={!Object.keys(changed).length} onClick={() => save.mutate()}>
          {Object.keys(changed).length
            ? Object.keys(changed).length > 1
              ? t('Save {n} changes', { n: Object.keys(changed).length })
              : t('Save {n} change', { n: Object.keys(changed).length })
            : t('Save')}
        </Button>
      </div>
    </div>
  );
}

function LogoInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [err, setErr] = useState<string | null>(null);
  const pick = (f: File | undefined) => {
    setErr(null);
    if (!f) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) return setErr(t('Use a PNG, JPG or WebP image.'));
    if (f.size > 500 * 1024) return setErr(t('The image is larger than 500 KB — please use a smaller one.'));
    const r = new FileReader();
    r.onload = () => onChange(String(r.result ?? ''));
    r.readAsDataURL(f);
  };
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex h-20 w-48 items-center justify-center rounded-lg bg-white p-2 ring-1 ring-slate-200">
        <img src={value || '/brand/logo.png'} alt={t('Logo preview')} className="max-h-full max-w-full object-contain" />
      </div>
      <div className="space-y-2">
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => pick(e.target.files?.[0])}
          className="block text-sm text-slate-600 file:me-3 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100"
          aria-label={t('Upload logo')}
        />
        {value && (
          <button type="button" className="text-xs font-medium text-brand-700 hover:underline" onClick={() => onChange('')}>
            {t('Use the built-in logo')}
          </button>
        )}
        {err && <p className="text-xs text-red-600">{err}</p>}
      </div>
    </div>
  );
}

type BackupInfo = { enabled: boolean; keepDays: number; hour: number; folder: string | null; backups: { name: string; size: number; createdAt: string }[] };

/** Automatic database backups: latest files and a "Back up now" button. */
function BackupsCard() {
  const qc = useQueryClient();
  const { fmt } = useTz();
  const q = useQuery({ queryKey: ['meta', 'backups'], queryFn: () => api.get<BackupInfo>('/meta/backups') });
  const now = useMutation({
    mutationFn: () => api.post<{ ok: boolean; error?: string; file?: string }>('/meta/backups', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta', 'backups'] }),
  });
  const b = q.data;
  return (
    <Card title={t('Backups')} actions={b?.enabled && <Button size="sm" variant="secondary" loading={now.isPending} onClick={() => now.mutate()}>{t('Back up now')}</Button>}>
      <ErrorBanner error={q.error ?? now.error} />
      {now.data && !now.data.ok && <Notice tone="amber">{now.data.error}</Notice>}
      {now.data?.ok && <Notice tone="green">{t('Backup made: {file}', { file: now.data.file })}</Notice>}
      {!b ? (
        <Spinner />
      ) : !b.enabled ? (
        <p className="text-sm text-slate-500">{t('Automatic backups are turned off on this server.')}</p>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">
            {/* {folder} is left in the translated text and replaced by the styled path below. */}
            {t(
              'A full copy of the database is made every day after {hour}:00 and kept for {days} days in {folder}. Copy that folder to another place (e.g. OneDrive or a USB drive) now and then — a backup on the same computer does not protect against losing the computer.',
              { hour: String(b.hour).padStart(2, '0'), days: b.keepDays },
            )
              .split('{folder}')
              .map((part, i) => (
                <span key={i}>
                  {i > 0 && <span className="font-mono text-xs">{b.folder}</span>}
                  {part}
                </span>
              ))}
          </p>
          {b.backups.length ? (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {b.backups.map((x) => (
                <li key={x.name} className="flex items-center justify-between px-3 py-2">
                  <span className="font-mono text-xs">{x.name}</span>
                  <span className="text-xs text-slate-500">{fmt(x.createdAt, null, 'dd LLL yyyy HH:mm')} · {t('{n} MB', { n: (x.size / 1e6).toFixed(1) })}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-500">{t('No backups yet — the first one is made tonight, or click “Back up now”.')}</p>
          )}
        </div>
      )}
    </Card>
  );
}
